import { collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  buildTakiDefaultRule,
  calculateFinanceCost,
  buildFinanceSnapshot,
  resolveProductCostRule,
  shouldAttachFinanceSnapshot
} from './financeCostEngine';
import { DEFAULT_FINANCE_COST_SETTINGS } from './financeCostConstants';
import { loadFinanceCostRules, loadFinanceCostSettings } from './financeCostFirestore';
import { getLiveHasRates } from './financeGoldRate';
import { catalogProductId } from './posCatalogItems';

/**
 * Fiyat akisi o an bos gelirse maliyet fotografi hic yazilamiyordu.
 * PriceContext ile ayni kaynaktan tek seferlik yedek cekim yapilir.
 */
const PRICES_URL = 'https://ukba-kasa.vercel.app/api/prices';

async function fetchPricesFallback() {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    let res;
    try {
      res = await fetch(PRICES_URL, {
        headers: { 'Cache-Control': 'no-cache' },
        signal: controller.signal
      });
    } finally {
      clearTimeout(timer);
    }
    if (!res?.ok) return [];
    const json = await res.json();
    const rows = Array.isArray(json?.data) ? json.data : [];
    return rows.map((row) =>
      row?.currencyCode ? { ...row, currencyCode: String(row.currencyCode).toUpperCase() } : row
    );
  } catch (err) {
    console.warn('finans yedek fiyat çekimi başarısız', err);
    return [];
  }
}

/**
 * Kural/ayar okumasi patlarsa da baglam uretilir: eskiden burada atilan hata
 * satisin maliyet fotografsiz kaydedilmesine yol aciyordu (rapor o satisi
 * sonradan BUGUNKU fiyatla hesapliyordu).
 */
export async function loadFinanceCostContext(sube, { prices } = {}) {
  const [rules, settings] = await Promise.all([
    loadFinanceCostRules(sube).catch((err) => {
      console.warn('maliyet kuralları okunamadı, kuralsız devam', err);
      return [];
    }),
    loadFinanceCostSettings(sube).catch((err) => {
      console.warn('maliyet ayarları okunamadı, varsayılanla devam', err);
      return { ...DEFAULT_FINANCE_COST_SETTINGS };
    })
  ]);
  let priceList = Array.isArray(prices) && prices.length ? prices : [];
  if (!priceList.length) priceList = await fetchPricesFallback();
  const liveHas = getLiveHasRates(priceList);
  return {
    rules,
    settings,
    rateContext: {
      rules,
      prices: priceList,
      liveHas,
      settings,
      defaultGoldRateSource: settings.defaultGoldRateSource
    }
  };
}

async function queryUrunlerByField(sube, field, value) {
  try {
    const urunlerRef = collection(db, 'subeler', sube, 'urunler');
    const snap = await getDocs(query(urunlerRef, where(field, '==', value)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data(), isCatalog: false }));
  } catch (err) {
    console.warn('finans ürün sorgusu başarısız', field, err);
    return [];
  }
}

/**
 * Barkodsuz Hızlı Takı: yalnızca tek ürün kartı varsa onu kullan.
 * Birden fazla SKU varsa tahmin etme; milyem kural/ayar katmanına kalır.
 */
export async function findUniqueProductForCatalogSale(sube, { kategori, urun } = {}) {
  const typeName = String(urun || '').trim();
  if (!sube || !typeName) return null;
  let rows = await queryUrunlerByField(sube, 'urunTipi', typeName);
  if (!rows.length) rows = await queryUrunlerByField(sube, 'isim', typeName);
  if (rows.length === 1) return rows[0];
  return null;
}

export async function loadProductForFinanceCost(sube, { barkod, kategori, urun } = {}) {
  const code = String(barkod || '').trim();
  if (code) {
    const rows = await queryUrunlerByField(sube, 'barkod', code);
    if (rows.length) return rows[0];
  }
  const unique = await findUniqueProductForCatalogSale(sube, { kategori, urun });
  if (unique) return unique;
  if (kategori && urun) {
    return {
      id: catalogProductId(kategori, urun),
      barkod: code || null,
      isim: urun,
      kategori,
      urunTipi: urun,
      gram: null,
      urunMilyem: null,
      isCatalog: true
    };
  }
  return null;
}

export async function attachFinanceSnapshotToIslem(islemData, { sube, prices, context } = {}) {
  if (!shouldAttachFinanceSnapshot(islemData)) return islemData;
  let ctx = context || await loadFinanceCostContext(sube, { prices });
  // Onceden yuklenen baglamda fiyat yoksa (sayfa acilirken akis gelmemisse)
  // fotograf bos cikardi; burada yedek fiyatla tamamlanir.
  if (!ctx?.rateContext?.prices?.length) {
    const fallbackPrices = await fetchPricesFallback();
    if (fallbackPrices.length) {
      ctx = {
        ...ctx,
        rateContext: {
          ...ctx.rateContext,
          prices: fallbackPrices,
          liveHas: getLiveHasRates(fallbackPrices)
        }
      };
    }
  }
  const product = await loadProductForFinanceCost(sube, {
    barkod: islemData.barkod,
    kategori: islemData.kategori,
    urun: islemData.urun
  });
  const subject = product || {
    id: null,
    barkod: islemData.barkod || null,
    isim: islemData.urun,
    kategori: islemData.kategori,
    urunTipi: islemData.urun,
    gram: islemData.gram,
    isCatalog: islemData.kategori !== 'Takı Ürün'
  };
  const resolved = resolveProductCostRule(subject, ctx.rateContext);
  // Kural tanimlanmamis barkodsuz takida varsayilan milyeme dus; eskiden
  // "maliyet kurali yok" deyip fotograf eksik kaliyordu.
  let rule = resolved.rule;
  let costSubject = subject;
  if (!rule) {
    const fallback = buildTakiDefaultRule(islemData, ctx.settings);
    if (fallback) {
      rule = fallback.rule;
      costSubject = { ...subject, urunMilyem: subject.urunMilyem || fallback.milyem };
    }
  }
  const calc = calculateFinanceCost({
    product: costSubject,
    islem: islemData,
    rule,
    rateContext: ctx.rateContext
  });
  return {
    ...islemData,
    financeSnapshot: buildFinanceSnapshot(calc, {
      computedAt: serverTimestamp(),
      saleAmountTry: islemData.toplamTutar
    })
  };
}
